import { useTranslation } from 'react-i18next';
import type { MediaScaleMode, ScreenRotationDegrees } from '@ghostscreen/contracts';

const OPTIONS: {
  value: ScreenRotationDegrees;
  key: 'landscape' | 'portrait' | 'portraitFlipped' | 'upsideDown';
}[] = [
  { value: 0, key: 'landscape' },
  { value: 90, key: 'portrait' },
  { value: 270, key: 'portraitFlipped' },
  { value: 180, key: 'upsideDown' },
];

type DisplayRotationControlProps = {
  value: ScreenRotationDegrees;
  disabled?: boolean;
  saving?: boolean;
  onChange: (degrees: ScreenRotationDegrees) => void;
};

function TvGlyph({ degrees }: { degrees: ScreenRotationDegrees }) {
  const portrait = degrees === 90 || degrees === 270;
  const upsideDown = degrees === 180;
  const flipped = degrees === 270;

  return (
    <span
      className="relative flex h-10 w-12 items-center justify-center"
      aria-hidden
    >
      <span
        className={[
          'rounded-[3px] border-2 border-current bg-panel2',
          portrait ? 'h-8 w-5' : 'h-5 w-8',
          upsideDown ? 'rotate-180' : '',
          flipped ? '-scale-x-100' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className="absolute left-1/2 top-0.5 h-0.5 w-2 -translate-x-1/2 rounded-full bg-current opacity-50" />
      </span>
    </span>
  );
}

/**
 * Operator control for desired presentation rotation on a physically rotated panel.
 * Degrees are the write API; orientation on the screen record stays a derived label.
 */
export function DisplayRotationControl({
  value,
  disabled = false,
  saving = false,
  onChange,
}: DisplayRotationControlProps) {
  const { t } = useTranslation('displays');
  const current = OPTIONS.find((option) => option.value === value);
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="mb-2 text-xs font-semibold text-muted">{t('rotation.legend')}</legend>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" aria-label={t('rotation.legend')}>
        {OPTIONS.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={t(`rotation.options.${option.key}.label`)}
              disabled={disabled || saving}
              title={t(`rotation.options.${option.key}.hint`)}
              onClick={() => {
                if (!selected) onChange(option.value);
              }}
              className={[
                'flex flex-col items-center gap-1.5 rounded-[12px] border px-2 py-2.5 text-center disabled:opacity-50',
                selected
                  ? 'border-signal bg-panel2 text-signal'
                  : 'border-line text-ink hover:border-[var(--gs-accent)]',
              ].join(' ')}
            >
              <TvGlyph degrees={option.value} />
              <span className="text-[11px] font-semibold leading-tight">
                {t(`rotation.options.${option.key}.short`)}
              </span>
            </button>
          );
        })}
      </div>
      <p className="m-0 mt-2 text-xs text-muted">
        {current ? t(`rotation.options.${current.key}.hint`) : null}
      </p>
    </fieldset>
  );
}
